import React, { useEffect, useMemo, useRef, useState } from "react";

import { Tooltip } from "@mui/material";
import { useQuery, useMutation } from "@tanstack/react-query";
import ShipeeziGrid, {
  RowActionsExtra,
  Constants as GridConstants,
  Icons as GridIcons,
} from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { FiEdit } from "react-icons/fi";
import { useHistory } from "react-router-dom";

import Button, { Color } from "../../../../../components/Form/Button";
import BadgeStatus from "../../../../../components/Generic/BadgeStatus";
import { NotificationToast } from "../../../../../components/Generic/Toast/NotificationToast";
import { PRIVATE_ROUTES } from "../../../../../constants";
import { useAppState } from "../../../../../context/StateContext";
import { Contact } from "../../../../../models/contact";
import { Customer, CustomerIds, Customer as ICustomer } from "../../../../../models/customer";
import { Location } from "../../../../../models/location";
import { Filters, Searches } from "../../../../../models/shipeezi.table";
import CustomerService from "../../../../../services/customer.service";
import { CustomerType, Status, UserStatus } from "../../../../../util/enums";
import InviteCustomerModal, { InviteCustomerModalHandles } from "./InviteCustomerModal";
import useStyles from "./styles";
import { checkResourceCan, FN_NAMES, hasPermission, isShowAction } from "Util/permissions";
import { ResourceName, ResourceCanEnum } from "Util/enums/accessRule";

export const CustomerScreen = () => {
  const classes = useStyles();
  const inviteModalRef = useRef<InviteCustomerModalHandles>(null);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState<Filters>();
  const [searches, setSearches] = useState<Searches>([]);
  const [rowsSelected, setRowsSelected] = useState<string[]>([]);

  const [total, setTotal] = useState<number>(0);

  const { currentUser, isFMVersion, userPermissions } = useAppState();

  const history = useHistory();

  const { t: translate } = useTranslation();
  const [customerPermission, setCustomerPermission] = useState<{
    view: boolean,
    edit: boolean,
    create: boolean,
    changeStatus: boolean,
  }>({
    view: false,
    edit: false,
    create: false,
    changeStatus: false,
  });

  const CACHE_TIME = 60 * 60 * 1000; // 1 hour

  useEffect(() => {
    if (userPermissions && userPermissions?.length > 0) {
      setCustomerPermission({
        view: hasPermission(userPermissions, FN_NAMES.FUNCTION_NETWORK_CUSTOMERS_VIEW),
        edit: checkResourceCan(userPermissions, ResourceName.CUSTOMERS, ResourceCanEnum.EDIT) && hasPermission(userPermissions, FN_NAMES.FUNCTION_NETWORK_CUSTOMERS_EDIT),
        create: checkResourceCan(userPermissions, ResourceName.CUSTOMERS, ResourceCanEnum.EDIT) && hasPermission(userPermissions, FN_NAMES.FUNCTION_NETWORK_CUSTOMERS_ADD_NEW),
        changeStatus: checkResourceCan(userPermissions, ResourceName.CUSTOMERS, ResourceCanEnum.EDIT) && hasPermission(userPermissions, FN_NAMES.FUNCTION_NETWORK_CUSTOMERS_CHANGE_STATUS),
      })
    }
  }, [userPermissions])

  const {
    data: customers,
    isFetching: loading,
    refetch: refetchCustomersList,
  } = useQuery(
    ["customers.list", page, filters, searches, pageSize],
    async () => {
      const response = await CustomerService.getAll({
        page,
        pageSize,
        companyId: currentUser?.companyId,
        include: JSON.stringify([
          { association: "contacts" },
          { association: "locations" },
          { association: "associatedCompany" },
        ]),
        searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      });

      if (response.data) return response.data;

      return [];
    },

    {
      enabled: !!currentUser?.id,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: CACHE_TIME,
    }
  );

  useQuery(
    ["customers.count", searches, filters],
    () =>
      CustomerService.count({
        ...filters,
        searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
        companyId: currentUser?.companyId,
      }),
    {
      enabled: !!currentUser?.id,
      onSuccess: (response) => setTotal(response?.data),
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: CACHE_TIME,
    }
  );

  const { refetch: refetchActiveCustomersCountList } = useQuery(
    ["customers.active.count"],
    () =>
      CustomerService.count({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
      }),
    { enabled: !!currentUser?.id }
  );

  const { mutate: updateCustomer } = useMutation(
    ["customers.status.update"],
    ({ id, customer }: { id: number; customer: ICustomer }) => CustomerService.update(id, customer),
    {
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        refetchCustomersList();
        refetchActiveCustomersCountList();
      },
    }
  );

  const rowsSelectedIsAvailableToInvite = useMemo(() => {
    const allRowsHaveNameAndEmailAndNotIsCompany = rowsSelected.every((rowId) => {
      const customer = customers?.find((customer) => Number(rowId) === Number(customer.id));

      if (!customer) return true;

      if (!customer.name) return false;
      if (!customer.email) return false;

      const find = customers?.find(
        (customer) =>
          Number(customer.id) === Number(rowId) &&
          (!customer?.associatedCompany ||
            customer?.associatedCompany?.status !== UserStatus.ACTIVE)
      );

      if (!find) return false;
      if (!find?.associatedCompanyId) return false;

      return true;
    });

    return allRowsHaveNameAndEmailAndNotIsCompany;
  }, [rowsSelected, customers]);

  const locationFormat = (locations: Location[]) => {
    if (locations && locations?.length > 0) {
      const location = locations.find((location) => location.type === "main");

      if (location && location?.country && location?.suburb) {
        return `${location.suburb} - ${location.country}`;
      }
    }

    return "";
  };

  const mainContactName = (contacts: Contact[]) => {
    if (contacts && contacts.length > 0) {
      const mainContact = contacts.find((contact) => contact.isPrimary === true);

      if (mainContact && mainContact?.lastName && mainContact?.firstName) {
        return `${mainContact.firstName} ${mainContact.lastName}`;
      }
    }

    return "";
  };

  const firstLastCustomerName = (name: string, customer: ICustomer) => {
    return customer?.fullName ?? `${name ?? ""} ${customer?.lastName ?? ""}`;
  };

  const handlePageSize = (pageSize: number) => {
    setPageSize(pageSize);
  };

  const handleCurrentPage = (numPage: number) => {
    document.location.href = "#top";
    setPage(numPage - 1);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const handleSearch = (values: Searches) => {
    setSearches(values);
    setPage(0);
  };

  const editCustomer = (customerId: number | string) => {
    if (customerId) {
      history.push({
        pathname: `${PRIVATE_ROUTES.NETWORK_CUSTOMER_MNT}/${customerId}`,
        state: {
          customerId: Number(customerId),
        },
      });
    }
  };

  const viewCustomer = (customerId: string | number) => {
    if (customerId) {
      history.push({
        pathname: `${PRIVATE_ROUTES.NETWORK_CUSTOMER_MNT}/${customerId}`,
        state: {
          viewOnly: true,
          customerId: Number(customerId),
        },
      });
    }
  };

  const activeOrInactiveCustomer = (id: string | number) => {
    if (customers) {
      const customerFound = customers.find((customer) => Number(customer.id) === Number(id));

      if (customerFound) {
        const customerUpdate: ICustomer = {
          ...customerFound,
          status: customerFound.status === Status.ACTIVE ? Status.INACTIVE : Status.ACTIVE,
        };

        if (customerUpdate?.id) updateCustomer({ id: customerUpdate.id, customer: customerUpdate });
      }
    }
  };

  const isCustomerActive = (id: string | number) => {
    if (customers) {
      const customerFound = customers.find((customer) => customer.id === id);

      if (customerFound) {
        return customerFound.status === Status.ACTIVE;
      }
    }
  };

  const onCallInviteCustomerModal = (customerIds: CustomerIds) => {
    inviteModalRef?.current?.open(customerIds);
  };

  const renderInviteCustomerButtonByRow = (customer: Customer) => {
    if (
      !isFMVersion ||
      !customer ||
      !customer?.associatedCompanyId ||
      (customer &&
        customer?.associatedCompany &&
        customer?.associatedCompany?.status === UserStatus.ACTIVE)
    ) {
      return null;
    }

    if (!customer.email) {
      return (
        <Tooltip
          data-cy="invite-customer"
          title={translate("thisCustomerDoesNotHaveAnEmailToSendInvitation")}
          placement="top"
        >
          <div>
            <GridIcons.email color="#D0D5DD" size={18} />
          </div>
        </Tooltip>
      );
    }

    if (customer.status === UserStatus.INACTIVE) {
      return (
        <Tooltip
          data-cy="invite-customer"
          title={translate("youCannotInviteInactiveCustomers")}
          placement="top"
        >
          <div>
            <GridIcons.email color="#D0D5DD" size={18} />
          </div>
        </Tooltip>
      );
    }

    return (
      <Tooltip data-cy="invite-customer" title={translate("inviteCustomer")} placement="top">
        <div>
          <GridIcons.email
            onClick={() => onCallInviteCustomerModal([Number(customer.id)])}
            color="#475467"
            className={classes.iconExtraAction}
            size={18}
          />
        </div>
      </Tooltip>
    );
  };

  const renderInviteCustomersButton = () => {
    if (!rowsSelected.length || !isFMVersion) return null;

    const tooltip = !rowsSelectedIsAvailableToInvite
      ? translate("allSelectedCustomersMustHaveNameEmailAndNotYetBelongToShipeezi")
      : "";

    return (
      <Tooltip title={tooltip}>
        <div>
          <Button
            data-cy="button-invite"
            customColor={Color.INFO}
            tooltip={`${translate("invite")} ${translate("customer")}`}
            disabled={!rowsSelectedIsAvailableToInvite}
            onClick={() => onCallInviteCustomerModal(rowsSelected.map((row) => Number(row)))}
          >
            <div className={classes.buttonInvite}>
              <GridIcons.email size={18} color="#3F6280" />
              <span>{translate("inviteCustomers")}</span>
            </div>
          </Button>
        </div>
      </Tooltip>
    );
  };

  const rowActionsExtra: RowActionsExtra[] = [
    {
      title: translate("edit"),
      key: "edit",
      buttonAndAction: (id: string | number) => {
        if (!isShowAction(currentUser, customerPermission.edit)) return null;

        if (isCustomerActive(id)) {
          return (
            <Tooltip title={translate("edit")} placement="top">
              <div>
                <FiEdit
                  className={classes.iconExtraAction}
                  size={GridConstants.defaultRowActionSize}
                  color={GridConstants.defaultColorIcon}
                  onClick={() => editCustomer(id)}
                  data-cy="action-edit"
                />
              </div>
            </Tooltip>
          );
        }
      },
    },
    {
      title: translate("inviteCustomer"),
      key: "inviteCustomer",
      buttonAndAction: (_, row: Customer) => renderInviteCustomerButtonByRow(row),
    },
  ];

  return (
    <div>
      <ShipeeziGrid
        data={customers ?? []}
        columns={[
          {
            name: "type",
            title: translate("type"),
            hideSearch: true,
          },
          {
            name: "name",
            title: translate("name"),
            formatComponent: firstLastCustomerName,
          },
          {
            name: "contacts",
            title: translate("mainContact"),
            formatComponent: mainContactName,
            hideSearch: true,
          },
          { name: "phone", title: translate("phone") },
          { name: "email", title: translate("email") },
          {
            name: "locations",
            title: translate("address"),
            formatText: locationFormat,
            hideSearch: true,
          },
          {
            name: "status",
            title: translate("status"),
            formatComponent: (value: UserStatus, customer: Customer) => {
              let status = value;

              if (status === UserStatus.INVITATION_SENT && customer?.expireInvitationDate) {
                const now = new Date();
                const isExpired =
                  new Date(customer?.expireInvitationDate).getTime() <= now.getTime();

                if (isExpired) status = UserStatus.INVITE_EXPIRED;
              }

              return <BadgeStatus value={status} className={classes.noWhiteSpace} />;
            },
            hideSearch: true,
          },
        ]}
        pageSize={pageSize}
        pageSizeList={[20, 40, 60, 100]}
        onChangePageSize={handlePageSize}
        loadingData={loading}
        totalItems={total}
        emptyData={translate("noInformationToBeShown")}
        currentPage={page}
        onNextPage={handleCurrentPage}
        onPreviousPage={handleCurrentPage}
        onGoToPage={handleCurrentPage}
        rowActionsExtra={rowActionsExtra}
        rowActions={{
          orderBy: ["edit", "handleDetails", "handleChange", "inviteCustomer"],
          handleDetails: isShowAction(currentUser, customerPermission.view)
            ? (rowId) => viewCustomer(rowId)
            : undefined,
          handleChange: isShowAction(currentUser, customerPermission.changeStatus)
            ? (rowId) => activeOrInactiveCustomer(rowId)
            : undefined,
        }}
        showSelection={true}
        positionActionsColumn={"right"}
        showHeaderGrid={true}
        showFooterGrid={true}
        generalActions={{
          orderBy: {
            left: ["handleSearch", "handleFilterBy"],
            right: ["handleNewRecord", "inviteCustomers"],
          },
          handleNewRecord: isShowAction(currentUser, customerPermission.create)
            ? {
              text: translate("create"),
              action: () => history.push(PRIVATE_ROUTES.NETWORK_CUSTOMER_MNT),
              tooltip: translate("create"),
            }
            : undefined,
          handleSearch: {
            onSearch: (filters) => handleSearch(filters),
          },
          handleFilterBy: {
            categories: [
              {
                name: "Type",
                options: [CustomerType.PRIVATE, CustomerType.CORPORATE],
              },
              {
                name: "Status",
                options: [Status.ACTIVE, Status.INACTIVE],
              },
            ],
            onChange: handleFilter,
          },
          inviteCustomers: renderInviteCustomersButton(),
        }}
        handleSelect={setRowsSelected}
      />
      <InviteCustomerModal ref={inviteModalRef} onSuccess={() => refetchCustomersList()} />
    </div>
  );
};
